import { ArrowDown, Maximize2, Minimize2, Pause, Play } from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { announce } from '../state/announcer.ts';
import {
  COMMAND_LIST,
  GLYPH_NAMES,
  MAX_RENDERED_EVENTS,
  boundEvents,
  commandSuggestions,
  durationLabel,
  eventTime,
  filterEvents,
  glyphFor,
  showingCount,
  type TerminalFilter,
} from '../features/engagement/model.ts';
import type { RunEvent } from '../features/engagement/types.ts';
import { Button } from './Button.tsx';
import { CopyButton } from './CopyButton.tsx';
import { MenuButton } from './Menu.tsx';
import { TooltipWrap } from './Tooltip.tsx';
import styles from './TerminalLog.module.css';

export interface TerminalLine {
  id: string;
  time: string;
  glyph: string;
  text: string;
  duration?: string;
  group?: number;
}

export interface TerminalCommands {
  /** Return false to keep the typed text in the field (unsupported input). */
  onSubmit: (input: string) => boolean | undefined;
  disabled?: boolean;
  /** Plain-text result of the last command, shown under the field (not live). */
  feedback?: string;
}

interface TerminalLogProps {
  title: string;
  lines: readonly TerminalLine[];
  /** Lines in this group are highlighted and scrolled into view. */
  highlightGroup?: number | null;
  /** Empty-state text before the first line. */
  emptyText?: string;
  compact?: boolean;
  // New in job 004 (§4.2.1): all optional so existing callers are untouched.
  /** Preferred event model; when present, lines is ignored. */
  events?: readonly RunEvent[];
  filter?: TerminalFilter;
  onFilterChange?: (f: TerminalFilter) => void;
  /** Y in "Showing X of Y events". */
  totalEvents?: number;
  selectedEventId?: string | null;
  onSelectEvent?: (id: string) => void;
  paused?: boolean;
  newEventCount?: number;
  onTogglePause?: () => void;
  onCopy?: (scope: 'filtered' | 'full') => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  truncated?: { shown: number; total: number };
  onLoadEarlier?: () => void;
  commands?: TerminalCommands;
  /** Names the engagement and run in the copy header (§4.4). */
  copyContext?: { engagement: string; runId: string };
  /** Dark activity console (window dots, mono title), as the workspace terminal. */
  variant?: 'console';
  /** Short run state shown as a pill in the header, e.g. "Running · step 2 of 4". */
  status?: ReactNode;
  statusTone?: 'live';
}

function plainLog(lines: readonly TerminalLine[]): string {
  return lines
    .map((l) => `${l.time}  ${l.glyph}  ${l.text}${l.duration ? `  ${l.duration}` : ''}`)
    .join('\n');
}

type EventLine = TerminalLine & { event: RunEvent };

function eventLine(e: RunEvent): EventLine {
  return {
    id: e.id,
    time: eventTime(e.at),
    glyph: glyphFor(e),
    text: e.text,
    duration: e.durationMs !== undefined ? durationLabel(e.durationMs) : undefined,
    event: e,
  };
}

// Shared terminal log (DESIGN_SYSTEM.md §10.14, WORKSPACE_COCKPIT_SPEC.md §9.2).
// Lines append without animation, wrap instead of scrolling sideways, and the
// view follows new lines only while the reader is at the bottom. The cockpit
// passes `events` for the run event model; existing callers keep `lines`.
export function TerminalLog({
  title,
  lines,
  highlightGroup = null,
  emptyText = 'No calls yet.',
  compact = false,
  events,
  filter,
  onFilterChange,
  totalEvents,
  selectedEventId,
  onSelectEvent,
  paused = false,
  newEventCount = 0,
  onTogglePause,
  onCopy,
  expanded = false,
  onToggleExpanded,
  truncated,
  onLoadEarlier,
  commands,
  copyContext,
  variant,
  status,
  statusTone,
}: TerminalLogProps) {
  const titleId = useId();
  const root = useRef<HTMLElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const commandInput = useRef<HTMLInputElement>(null);
  const expandButton = useRef<HTMLButtonElement>(null);
  const [following, setFollowing] = useState(true);
  const [layout, setLayout] = useState<'single-row' | 'stacked'>(
    compact ? 'stacked' : 'single-row',
  );
  const [copyState, setCopyState] = useState<'idle' | 'manual'>('idle');
  // Lines the reader has scrolled past; anything newer while not following is unseen.
  const [seen, setSeen] = useState(0);
  const [command, setCommand] = useState('');
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [rejected, setRejected] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // --- Event model view ---
  const usesEvents = events !== undefined;
  const bounded = useMemo(
    () => (usesEvents ? boundEvents(events ?? []) : { events: [], dropped: 0 }),
    [events, usesEvents],
  );
  const allEvents = bounded.events;
  const filteredEvents = useMemo(
    () => (filter && usesEvents ? filterEvents(allEvents, filter) : allEvents),
    [allEvents, filter, usesEvents],
  );
  const eventLines = useMemo(() => filteredEvents.map(eventLine), [filteredEvents]);
  const shownCount = usesEvents ? filteredEvents.length : lines.length;
  const totalCount = usesEvents ? (totalEvents ?? allEvents.length) : lines.length;
  // Roving tabindex: the selected row when it is shown, else the first row.
  const tabStopId =
    eventLines.find((l) => l.id === selectedEventId)?.id ?? eventLines[0]?.id ?? null;
  const zeroMatches = usesEvents && filter !== undefined && filteredEvents.length === 0;

  // --- Container-driven layout (§4.5): the switch follows the container, never the viewport ---
  useEffect(() => {
    const el = root.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setLayout(entry.contentRect.width < 640 ? 'stacked' : 'single-row');
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Follow the tail only while at the bottom; paused presentation pins the view.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && following && !paused) el.scrollTop = el.scrollHeight;
  }, [shownCount, following, paused]);

  // Escape leaves focus mode and returns focus to the Expand control (§4.5).
  useEffect(() => {
    if (!expanded || !onToggleExpanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      onToggleExpanded();
      expandButton.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, onToggleExpanded]);

  // Resume returns to the live tail.
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused) {
      const el = scroller.current;
      if (el) el.scrollTop = el.scrollHeight;
      setFollowing(true);
      setSeen(shownCount);
    }
    wasPaused.current = paused;
  }, [paused, shownCount]);

  // Keep the visible anchor when an earlier page is prepended (§4.5).
  const anchor = useRef<{ first: string | null; height: number }>({ first: null, height: 0 });
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = eventLines[0]?.id ?? null;
    const prevFirst = anchor.current.first;
    const prepended =
      prevFirst !== null && first !== prevFirst && eventLines.some((l) => l.id === prevFirst);
    if (prepended && !following) el.scrollTop += el.scrollHeight - anchor.current.height;
    anchor.current = { first, height: el.scrollHeight };
  });

  useEffect(() => {
    if (highlightGroup === null) return;
    const el = scroller.current?.querySelector<HTMLElement>(`[data-group="${highlightGroup}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightGroup]);

  useEffect(() => {
    if (selectedEventId === null || selectedEventId === undefined) return;
    scroller.current
      ?.querySelector<HTMLElement>(`[data-event-id="${selectedEventId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedEventId]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.clientHeight - el.scrollTop < 24;
    setFollowing(atBottom);
    if (atBottom) setSeen(shownCount);
  };

  const jump = () => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setFollowing(true);
    setSeen(shownCount);
  };

  // --- Copy (§4.4): context header, filtered marker, clipboard fallback ---
  const copyText = (scope: 'filtered' | 'full') => {
    if (!usesEvents) return plainLog(lines);
    const source = scope === 'filtered' ? filteredEvents : allEvents;
    const first = events?.[0];
    const attempt = first ? `attempt ${first.attempt}` : 'selected run';
    const header = [
      `Traceira terminal log — ${title}`,
      copyContext?.engagement ? `Engagement: ${copyContext.engagement}` : null,
      `Run: ${copyContext?.runId ? `${copyContext.runId} · ` : ''}${attempt}`,
      filter && scope === 'filtered' ? `Filter: ${filter}` : null,
      `${showingCount(source.length, totalCount)}${scope === 'filtered' ? ' · filtered view' : ''}`,
    ]
      .filter(Boolean)
      .join('\n');
    const body = source
      .map(
        (e) =>
          `${eventTime(e.at)}  ${glyphFor(e)}  ${e.actor}  ${e.stepId ?? ''}  ${e.text}${e.evidenceId ? `  ${e.evidenceId}` : ''}${e.durationMs !== undefined ? `  ${durationLabel(e.durationMs)}` : ''}`,
      )
      .join('\n');
    return `${header}\n${body}`;
  };

  const handleCopy = async (scope: 'filtered' | 'full') => {
    onCopy?.(scope);
    const value = copyText(scope);
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (ok) {
      setCopyState('idle');
      announce(
        scope === 'filtered'
          ? `Filtered log copied (${filteredEvents.length} of ${totalCount} events).`
          : `Log copied (${totalCount} events).`,
      );
    } else {
      // Clipboard denied: select the visible log text and tell the reader.
      setCopyState('manual');
      const el = scroller.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      announce('Clipboard unavailable. Log text selected; press Ctrl+C, or ⌘C on Mac, to copy.');
    }
  };

  // --- Command footer (§4.5) ---
  const suggestions = useMemo(
    // After an unsupported command, list everything that is supported.
    () => commandSuggestions(rejected ? '' : command),
    [command, rejected],
  );

  const suggestionsVisible =
    usesEvents && suggestions.length > 0 && !dismissed && (rejected || command.trim() !== '');

  const submitCommand = (value = command) => {
    if (commands?.disabled) return;
    const keep = commands?.onSubmit(value) === false;
    setRejected(keep);
    setDismissed(false);
    if (!keep) setCommand('');
    setSuggestionIndex(0);
  };

  const filterButtons: { id: TerminalFilter; label: string }[] = [
    { id: 'activity', label: 'Activity' },
    { id: 'requests', label: 'Requests' },
    { id: 'decisions', label: 'Decisions' },
  ];

  return (
    <section
      ref={root}
      className={[
        styles.terminal,
        compact ? styles.compact : '',
        expanded ? styles.expanded : '',
        variant === 'console' ? styles.console : '',
      ].join(' ')}
      aria-labelledby={titleId}
      data-layout={compact ? 'stacked' : layout}
      data-expanded={expanded || undefined}
    >
      <header className={styles.header}>
        {variant === 'console' && (
          <span className={styles.windowDots} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        )}
        <h3 id={titleId} className={styles.title}>
          {title}
        </h3>
        {status && (
          <span className={styles.statusPill} data-tone={statusTone}>
            {status}
          </span>
        )}
        {usesEvents ? (
          <span className={styles.count}>{showingCount(shownCount, totalCount)}</span>
        ) : (
          <span className={styles.count}>
            {lines.length} {lines.length === 1 ? 'line' : 'lines'}
          </span>
        )}
        {usesEvents && filter && onFilterChange && (
          <div className={styles.filters} role="group" aria-label="Terminal view filter">
            {filterButtons.map((f) => (
              <Button
                key={f.id}
                size="small"
                variant={filter === f.id ? 'secondary' : 'tertiary'}
                aria-pressed={filter === f.id}
                onPress={() => onFilterChange(f.id)}
                className={styles.filterButton}
              >
                {f.label}
              </Button>
            ))}
          </div>
        )}
        <span className={styles.actions}>
          {usesEvents && onTogglePause && (
            <Button size="small" variant="tertiary" onPress={onTogglePause} aria-pressed={paused}>
              {paused ? (
                <Play size={16} aria-hidden="true" />
              ) : (
                <Pause size={16} aria-hidden="true" />
              )}
              {paused ? 'Resume updates' : 'Pause updates'}
            </Button>
          )}
          {usesEvents && onToggleExpanded && (
            <Button
              ref={expandButton}
              size="small"
              variant="tertiary"
              onPress={onToggleExpanded}
              aria-pressed={expanded}
              aria-label={expanded ? 'Exit expanded terminal' : 'Expand terminal'}
            >
              {expanded ? (
                <Minimize2 size={16} aria-hidden="true" />
              ) : (
                <Maximize2 size={16} aria-hidden="true" />
              )}
              {expanded ? 'Exit' : 'Expand'}
            </Button>
          )}
          {usesEvents ? (
            <MenuButton
              label={copyState === 'manual' ? 'Select and copy log' : 'Copy log'}
              items={[
                { id: 'copy:filtered', label: 'Copy filtered view' },
                { id: 'copy:full', label: 'Copy full log' },
              ]}
              onSelect={(id) => {
                if (id === 'copy:filtered') void handleCopy('filtered');
                if (id === 'copy:full') void handleCopy('full');
              }}
            >
              {copyState === 'manual' ? 'Select and copy' : 'Copy'}
            </MenuButton>
          ) : (
            <span className={styles.copy}>
              <CopyButton value={plainLog(lines)} what="log" label="Copy log" showIcon />
            </span>
          )}
        </span>
      </header>

      {paused && usesEvents && (
        <p className={styles.pauseNote}>
          Updates paused · {newEventCount} new events · work continues
        </p>
      )}

      {usesEvents && (
        <span id={`${titleId}-row-hint`} className="sr-only">
          Arrow keys move between events. Enter or Space opens the event in the inspector.
        </span>
      )}
      {/* A focusable scroll region so keyboard users can scroll the log (spec §4.6). */}
      <div
        ref={scroller}
        className={styles.scroller}
        role="log"
        aria-labelledby={titleId}
        aria-describedby={usesEvents ? `${titleId}-row-hint` : undefined}
        aria-live="off"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onScroll={onScroll}
      >
        {truncated && (
          <div className={styles.truncatedRow}>
            <span>
              History truncated · showing the most recent {truncated.shown} of {truncated.total}{' '}
              events
            </span>
            {onLoadEarlier ? (
              <TooltipWrap
                content={`The backend keeps the full history; this view renders at most ${MAX_RENDERED_EVENTS} rows so it stays responsive.`}
              >
                <Button size="small" variant="tertiary" onPress={onLoadEarlier}>
                  Load earlier events
                </Button>
              </TooltipWrap>
            ) : (
              <span>
                The view is capped at {MAX_RENDERED_EVENTS} rows; earlier events stay recorded.
              </span>
            )}
          </div>
        )}
        {zeroMatches ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>No events match this filter.</p>
            {onFilterChange && (
              <Button size="small" variant="tertiary" onPress={() => onFilterChange('activity')}>
                Clear filters
              </Button>
            )}
          </div>
        ) : shownCount === 0 ? (
          <p className={styles.empty}>{emptyText}</p>
        ) : (
          <ol className={styles.lines}>
            {(usesEvents ? eventLines : lines).map((l) => {
              const event = 'event' in l ? (l as EventLine).event : null;
              const selected = event && event.id === selectedEventId;
              return (
                <li
                  key={l.id}
                  className={[
                    styles.line,
                    onSelectEvent && event ? styles.selectable : '',
                    selected ? styles.selected : '',
                  ].join(' ')}
                  data-glyph={l.glyph}
                  data-group={l.group}
                  data-event-id={event?.id}
                  data-selected={selected || undefined}
                  data-highlight={
                    highlightGroup !== null && l.group === highlightGroup ? true : undefined
                  }
                  {...(onSelectEvent && event
                    ? {
                        // Roving tabindex: the selected row, else the first row.
                        tabIndex: event.id === tabStopId ? 0 : -1,
                        'aria-current': selected ? ('true' as const) : undefined,
                        onClick: () => onSelectEvent(event.id),
                        onKeyDown: (e: ReactKeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectEvent(event.id);
                          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            const rows = Array.from(
                              scroller.current?.querySelectorAll<HTMLElement>('[data-event-id]') ??
                                [],
                            );
                            const idx = rows.indexOf(e.currentTarget as HTMLElement);
                            const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
                            rows[Math.min(Math.max(next, 0), rows.length - 1)]?.focus();
                          }
                        },
                      }
                    : {})}
                >
                  <time className={styles.time}>{l.time}</time>
                  <span
                    className={styles.glyph}
                    aria-label={GLYPH_NAMES[l.glyph] ?? undefined}
                    role="img"
                  >
                    {l.glyph}
                  </span>
                  {event && <span className={styles.actor}>{event.actor}</span>}
                  {event?.stepId && <span className={styles.step}>{event.stepId}</span>}
                  <span className={styles.text} data-part="text">
                    {l.text}
                  </span>
                  {event?.evidenceId && (
                    <button
                      type="button"
                      className={styles.evidence}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEvent?.(event.id);
                      }}
                    >
                      {event.evidenceId}
                    </button>
                  )}
                  {l.duration && <span className={styles.duration}>{l.duration}</span>}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {copyState === 'manual' && (
        <p className={styles.manualHint}>
          Clipboard unavailable · press Ctrl+C (⌘C on Mac) to copy the selected log text.
        </p>
      )}

      {!following && !paused && shownCount > seen && (
        <div className={styles.jump}>
          <Button size="small" onPress={jump}>
            <ArrowDown size={16} aria-hidden="true" />
            {usesEvents ? `${shownCount - seen} new events · Jump to latest` : 'Jump to latest'}
          </Button>
        </div>
      )}

      {commands && (
        <form
          className={styles.commandFooter}
          onSubmit={(e) => {
            e.preventDefault();
            // A partial name with visible suggestions only completes to the
            // highlighted one; a second Enter runs it.
            const pick = !rejected && suggestionsVisible ? suggestions[suggestionIndex] : undefined;
            if (pick && command.trim() !== pick) {
              setCommand(`${pick} `);
              setSuggestionIndex(0);
              return;
            }
            submitCommand();
          }}
        >
          <label
            className={variant === 'console' ? 'sr-only' : styles.commandLabel}
            htmlFor={`${titleId}-command`}
          >
            Command
          </label>
          {variant === 'console' && (
            <span className={styles.prompt} aria-hidden="true">
              ›
            </span>
          )}
          <input
            id={`${titleId}-command`}
            ref={commandInput}
            className={styles.commandInput}
            type="text"
            value={command}
            disabled={commands.disabled}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={suggestionsVisible}
            aria-controls={suggestionsVisible ? `${titleId}-suggestions` : undefined}
            aria-activedescendant={
              suggestionsVisible ? `${titleId}-suggestion-${suggestionIndex}` : undefined
            }
            aria-autocomplete="list"
            aria-describedby={`${titleId}-command-hint`}
            placeholder="/help lists the supported commands"
            onChange={(e) => {
              setCommand(e.target.value);
              setSuggestionIndex(0);
              setRejected(false);
              setDismissed(false);
            }}
            onKeyDown={(e) => {
              const count = Math.max(suggestions.length, 1);
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSuggestionIndex((i) => (i + 1) % count);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSuggestionIndex((i) => (i - 1 + count) % count);
              } else if (e.key === 'Escape' && suggestionsVisible) {
                e.preventDefault();
                e.stopPropagation();
                setDismissed(true);
              }
            }}
          />
          <span id={`${titleId}-command-hint`} className="sr-only">
            Workspace commands: {COMMAND_LIST}
          </span>
          <Button type="submit" size="small" isDisabled={commands.disabled}>
            Run
          </Button>
          {suggestionsVisible && (
            <div
              id={`${titleId}-suggestions`}
              className={styles.suggestions}
              role="listbox"
              aria-label="Suggestions"
            >
              {suggestions.map((sug, i) => (
                // Keyboard use stays in the input (arrows + Enter); a click completes it.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key={sug}
                  id={`${titleId}-suggestion-${i}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={i === suggestionIndex}
                  className={styles.suggestion}
                  data-active={i === suggestionIndex || undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setCommand(`${sug} `);
                    commandInput.current?.focus();
                  }}
                >
                  {sug}
                </div>
              ))}
            </div>
          )}
          {commands.feedback && <p className={styles.commandFeedback}>{commands.feedback}</p>}
        </form>
      )}
    </section>
  );
}
